import { fetchCachedAuthUser } from "../utils/authCache";
import React, { useEffect, useMemo, useState } from "react";
import {
  usePreferences,
} from "../context/PreferencesContext";
import {
  getAvaaioUiCopy,
} from "../utils/avaaioUiCopy";

type MeShape = {
  email?: string;
  role?: string;
  subscription_status?: string;
  memory?: any;
};

export default function SettingsPlanPanel() {
  const [me, setMe] = useState<MeShape | null>(null);
  const { preferences } = usePreferences();
  const interfaceCopy = getAvaaioUiCopy(
    preferences.language
  );

  useEffect(() => {
    fetchCachedAuthUser("/api/v1/auth/me", { credentials: "include" })
            .then((data) => {
        if (!data) return;
        const userData = data?.user || data;
        setMe(userData);
      })
      .catch(() => {});
  }, []);

  const role = String(me?.role || "").toLowerCase();
  const sub = String(me?.subscription_status || "").toLowerCase();

  const isAdmin = [
    "admin",
    "owner",
    "superadmin",
    "super_admin",
  ].includes(role);

  const isPro =
    !isAdmin &&
    (
      role === "vip" ||
      role === "pro" ||
      sub === "active"
    );

  const isFree =
    !isAdmin &&
    !isPro;

  const displayName = useMemo(() => {
    return (
      me?.memory?.display_name ||
      me?.email ||
      interfaceCopy.plan.accountFallback
    );
  }, [
    me,
    interfaceCopy.plan.accountFallback,
  ]);

  const sectionStyle: React.CSSProperties = {
    maxWidth: 1100,
    margin: "20px auto 16px auto",
    padding: "0 16px",
  };

  const shellStyle: React.CSSProperties = {
    border: "1px solid rgba(90,210,255,0.28)",
    borderRadius: 20,
    background: "rgba(6,10,18,0.72)",
    boxShadow: "0 12px 48px rgba(0,0,0,0.35)",
    backdropFilter: "blur(10px)",
    padding: 18,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 800,
    color: "#dff7ff",
    marginBottom: 6,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: 13,
    color: "rgba(220,238,255,0.72)",
    marginBottom: 16,
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  };

  const cardBase: React.CSSProperties = {
    borderRadius: 18,
    padding: 16,
    minHeight: 160,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.03)",
  };

  const headingStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 800,
    marginBottom: 6,
  };

  const textStyle: React.CSSProperties = {
    fontSize: 13,
    lineHeight: 1.45,
    color: "rgba(230,240,255,0.82)",
  };

  const badgeStyle = (bg: string, color: string): React.CSSProperties => ({
    display: "inline-block",
    marginTop: 12,
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    background: bg,
    color,
  });

  const actionBtn: React.CSSProperties = {
    marginTop: 14,
    padding: "10px 12px",
    borderRadius: 12,
    border: "none",
    fontWeight: 800,
    cursor: "pointer",
    background: "linear-gradient(135deg, #b266ff, #6df2ff)",
    color: "#041019",
  };

  

  async function handleUpgradeToPro() {
    try {
      if (!(window as any).webkit || !(window as any).webkit.messageHandlers || !(window as any).webkit.messageHandlers?.purchase) {
        alert(interfaceCopy.plan.iosOnly);
        return;
      }

      // Trigger native iOS StoreKit purchase
      (window as any).webkit.messageHandlers?.purchase.postMessage({
        productId: "avaaio.pro.monthly"
      });

    } catch (err) {
      console.error(err);
      alert(interfaceCopy.plan.purchaseError);
    }
  }

return (
    <section style={sectionStyle}>
      <div style={shellStyle}>
        <div style={titleStyle}>{interfaceCopy.plan.title}</div>
        <div style={subtitleStyle}>
          {interfaceCopy.plan.statusPrefix}{" "}
            <strong>{displayName}</strong>.{" "}
            {interfaceCopy.plan.subtitle}
        </div>

        <div style={gridStyle}>
          {isFree && (
          <div
            style={{
              ...cardBase,
              border: "1px solid rgba(80,220,255,0.35)",
              boxShadow: isFree ? "0 0 0 1px rgba(80,220,255,0.18) inset" : "none",
            }}
          >
            <div style={{ ...headingStyle, color: "#7feaff" }}>{interfaceCopy.plan.freeTitle}</div>
            <div style={textStyle}>
              {interfaceCopy.plan.freeDescription}
            </div>
            {isFree && <div style={badgeStyle("rgba(255,215,0,0.16)", "#ffd34d")}>{interfaceCopy.plan.active}</div>}
          </div>
          )}

          {(isFree || isPro) && (
          <div
            style={{
              ...cardBase,
              border: "1px solid rgba(190,120,255,0.35)",
              boxShadow: isPro ? "0 0 0 1px rgba(190,120,255,0.18) inset" : "none",
            }}
          >
            <div style={{ ...headingStyle, color: "#d4a7ff" }}>{interfaceCopy.plan.proTitle}</div>
            <div style={textStyle}>
              {interfaceCopy.plan.proDescription}
            </div>
            {isPro && <div style={badgeStyle("rgba(80,255,170,0.16)", "#71ffb3")}>{interfaceCopy.plan.active}</div>}
            {isFree && (
              <button onClick={handleUpgradeToPro}
                type="button"
                style={actionBtn}
                
              >
                {interfaceCopy.plan.unlockPro}
              </button>
            )}
          </div>
          )}

          {isAdmin && (
            <div
              style={{
                ...cardBase,
                border: "1px solid rgba(255,100,120,0.35)",
                boxShadow: "0 0 0 1px rgba(255,100,120,0.18) inset",
              }}
            >
              <div style={{ ...headingStyle, color: "#ff8f9d" }}>{interfaceCopy.plan.adminTitle}</div>
              <div style={textStyle}>
                {interfaceCopy.plan.adminDescription}
              </div>
              <div style={badgeStyle("rgba(255,90,110,0.16)", "#ff9cab")}>{interfaceCopy.plan.active}</div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}